import React from 'react';
import * as Yup from 'yup';
import {Form, Row} from 'react-bootstrap';
import {useIntl} from 'react-intl';
import {useFormik} from 'formik';
import toast from 'react-hot-toast';
import CustomModal from '../../../app/common/components/modal/CustomModal';
import ButtonSubmit from '../../../app/modules/form/ButtonSubmit';
import ButtonReset from '../../../app/modules/form/ButtonReset';
import {useSendDivopsAssistance} from '../Hooks/DivopsHooks';

interface DivopsAssistanceFormValues {
  objet: string;
  description: string;
  adressePostale: string;
}

const DEFAULT_VALUES: DivopsAssistanceFormValues = {
  objet: '',
  description: '',
  adressePostale: '',
};

interface DivopsAssistanceModalProps {
  show: boolean;
  hide: () => void;
}

const DivopsAssistanceModal = ({show, hide}: DivopsAssistanceModalProps): JSX.Element => {
  const intl = useIntl();
  const requiredFieldMessage = intl.formatMessage({id: 'FIELD.REQUIRED'});
  const {mutate, isLoading} = useSendDivopsAssistance();

  const validationSchema = Yup.object().shape({
    objet: Yup.string().required(requiredFieldMessage),
    description: Yup.string().required(requiredFieldMessage),
    adressePostale: Yup.string(),
  });

  const formik = useFormik<DivopsAssistanceFormValues>({
    initialValues: DEFAULT_VALUES,
    validationSchema,
    onSubmit: (values) => {
      mutate(
        {
          objet: values.objet,
          description: values.description,
          adressePostale: values.adressePostale || undefined,
        },
        {
          onSuccess: () => {
            toast.success(intl.formatMessage({id: 'DIVOPS.ASSISTANCE.SUCCESS'}));
            formik.resetForm();
            hide();
          },
          onError: () => {
            toast.error(intl.formatMessage({id: 'DIVOPS.ASSISTANCE.ERROR'}));
          },
        }
      );
    },
  });

  const handleClose = () => {
    formik.resetForm();
    hide();
  };

  return (
    <CustomModal
      show={show}
      onHide={handleClose}
      title={intl.formatMessage({id: 'DIVOPS.ASSISTANCE.TITLE'})}
      body={
        <>
          <Row className="mb-3">
            <Form.Group>
              <Form.Label className="fw-bold fs-3 required">
                {intl.formatMessage({id: 'DIVOPS.ASSISTANCE.OBJET'})}
              </Form.Label>
              <Form.Control
                type="text"
                name="objet"
                value={formik.values.objet}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={formik.touched.objet && !!formik.errors.objet}
              />
              {formik.touched.objet && formik.errors.objet && (
                <Form.Control.Feedback type="invalid">{formik.errors.objet}</Form.Control.Feedback>
              )}
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group>
              <Form.Label className="fw-bold fs-3 required">
                {intl.formatMessage({id: 'DIVOPS.ASSISTANCE.DESCRIPTION'})}
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                name="description"
                value={formik.values.description}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={formik.touched.description && !!formik.errors.description}
              />
              {formik.touched.description && formik.errors.description && (
                <Form.Control.Feedback type="invalid">
                  {formik.errors.description}
                </Form.Control.Feedback>
              )}
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group>
              <Form.Label className="fw-bold fs-3">
                {intl.formatMessage({id: 'DIVOPS.ASSISTANCE.ADRESSE'})}
              </Form.Label>
              <Form.Control
                type="text"
                name="adressePostale"
                value={formik.values.adressePostale}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            </Form.Group>
          </Row>
        </>
      }
      footer={
        <>
          <ButtonReset onClick={handleClose} textId="DIVOPS.ASSISTANCE.CANCEL" size="small" />
          <ButtonSubmit
            disabled={formik.isSubmitting}
            isLoading={isLoading}
            textId="DIVOPS.ASSISTANCE.SUBMIT"
            size="small"
            onClick={formik.submitForm}
          />
        </>
      }
    />
  );
};

export default DivopsAssistanceModal;
